import { useState, useEffect } from "react";
import styled from "styled-components";
import XMaskIcon from "../../icons/XMark";
import Avatar from "../../components/Avatar";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";
import DropdownLayoutV2 from "../../components/DropdownLayoutV2";
import DefaultAvatar from "../../components/DefaultAvatar";
import { ChevronsUpDownIcon } from "lucide-react";

const UserItem = styled.div`
  padding: 4px;
  display: flex;
  align-items: center;
  position: relative;
  &:hover {
    background: #edebe9;
  }

  div {
    color: rgb(51, 51, 51);
    font-weight: 400;
    font-size: 14px;
    padding: 0px 4px;
    height: 30px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
  }
`;

const Label = styled.div`
  font-weight: 700;
  font-size: 12px;
  color: #323130;
  padding: 8px;
`;

const ButtonClear = styled.div`
  position: absolute;
  right: 6px;
`;

export const InputSearch = styled.input`
  border: 1px solid #d1d5db;
  height: 32px;
  width: 100%;
  outline: none;
  padding: 5px;

  &:focus {
    border: 1px solid #2563eb;
  }
`;

const AssignTo = (props: any) => {
  const {
    assignees = fromJS([]),
    onChange,
    border = false,
    positionRight = false,
    disabled = false,
  } = props;

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userList, setUserList] = useState([]);
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));

  const selectUser = (user: any) => {
    if (!assignedUsers.includes(user)) {
      assignedUsers.push(user);
      setAssignedUsers(assignedUsers);
      onChange(assignedUsers);
    }
  };

  const removeUser = (userId: string) => {
    const selectedUsers = assignedUsers.filter((item: any) => item.accountId !== userId);
    setAssignedUsers(selectedUsers);
    onChange(selectedUsers);
  };

  const isAssignedUser = (userId) => {
    const index = assignedUsers.findIndex((item) => item.accountId == userId);
    return index != -1;
  };

  const onSearch = (e: any) => {
    const { value = "" } = e.target;
    const users = members.filter((user: any) => user.get("displayName").includes(value));
    setUserList(users.toJS());
  };

  useEffect(() => {
    if (assignees.size > 0) {
      const users = members.filter((user) => assignees.includes(user.get("accountId")));
      setAssignedUsers(users.toJS());
    }
    setUserList(members.toJS());
  }, [members, assignees]);

  const renderAssignedUsers = () => {
    if (assignedUsers.length === 0) {
      return null;
    }
    return (
      <>
        <Label>Assign To</Label>
        {assignedUsers.map((item: any) => {
          const { accountId, displayName, avatarUrl } = item;
          return (
            <UserItem key={accountId}>
              <Avatar avatarUrl={avatarUrl} />
              <div>{displayName}</div>
              <ButtonClear className="btn-clear" onClick={() => removeUser(accountId)}>
                <XMaskIcon size={18} color="#31752f" />
              </ButtonClear>
            </UserItem>
          );
        })}
      </>
    );
  };

  const renderSearchedUsers = () => {
    const users = userList.filter((user) => !isAssignedUser(user.accountId));
    if (users.length === 0) {
      return null;
    }
    return (
      <>
        <Label>All members</Label>
        {users.map((user: any) => {
          const { accountId, displayName, avatarUrl } = user;
          return (
            <UserItem className="user-item" key={accountId} onClick={() => selectUser(user)}>
              <Avatar avatarUrl={avatarUrl} />
              <div>{displayName}</div>
            </UserItem>
          );
        })}
      </>
    );
  };

  const renderUsers = () => {
    if (assignedUsers.length == 0) {
      return null;
    }

    if (assignedUsers.length == 1) {
      const user = assignedUsers[0];
      return (
        <div className="flex items-center gap-0.5 min-w-0">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="w-[26px] h-[26px] rounded-full object-cover shrink-0"
            />
          ) : (
            <DefaultAvatar
              name={user.displayName}
              size="sm"
              className="w-[26px] h-[26px] rounded-full bg-blue-100 text-blue-800 text-xs font-semibold flex items-center justify-center shrink-0"
            />
          )}
          <span className="text-sm font-medium text-gray-700 leading-5 tracking-tight truncate">
            {user.displayName}
          </span>
        </div>
      );
    }

    // Multiple users - show stacked avatars
    const visibleUsers = assignedUsers.slice(0, 3);
    return (
      <div className="flex items-center shrink-0">
        {visibleUsers.map((user: any, index: number) => (
          <div
            key={user.accountId}
            className="relative"
            style={{ marginLeft: index > 0 ? "-16px" : "0" }}
          >
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="w-[26px] h-[26px] rounded-full object-cover border-2 border-white"
              />
            ) : (
              <DefaultAvatar
                name={user.displayName}
                size="sm"
                className="w-[26px] h-[26px] rounded-full bg-blue-100 text-blue-800 text-xs font-semibold flex items-center justify-center border-2 border-white"
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPlaceholder = () => {
    if (assignedUsers && assignedUsers.length > 0) {
      return renderUsers();
    }
    return (
      <span className="text-sm font-medium text-gray-500 leading-5 tracking-tight">
        Select a value
      </span>
    );
  };
  const borderCSS = border ? "border border-gray-300" : "";
  const activator = (
    <div
      className={`btn-show-list-user w-full cursor-pointer flex items-center ${borderCSS} h-9 rounded-sm focus:outline-none focus:ring-0`}
    >
      <div
        className={`flex items-center flex-1 min-w-0 overflow-hidden px-2 ${
          positionRight ? "justify-end" : ""
        }`}
      >
        {renderPlaceholder()}
      </div>
      <div className="shrink-0 px-2">
        <ChevronsUpDownIcon className="w-3.5 h-3.5 text-gray-500" />
      </div>
    </div>
  );
  if (disabled) {
    return activator;
  }
  return (
    <>
      <DropdownLayoutV2 activator={activator}>
        <div className={"w-full p-2 text-left"}>
          <InputSearch onChange={onSearch} placeholder={"Keywords"} />
          {renderAssignedUsers()}
          {renderSearchedUsers()}
        </div>
      </DropdownLayoutV2>
    </>
  );
};

export default AssignTo;
